import React, { useState, useEffect } from 'react';
import { X, Calendar, AlertCircle, Check, Loader2, Save, User } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../services/api';

const ModalMoisNonScolarises = ({ 
  famille, 
  anneeScolaireId, 
  moisScolaires,
  elevesFamille,
  showNotification,
  onClose, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [moisNonScolarises, setMoisNonScolarises] = useState({});
  const [moisPayes, setMoisPayes] = useState({}); 
  // Structure : { eleve_id: [mois_id1, mois_id2, ...], ... }
  
  const [modificationsEnCours, setModificationsEnCours] = useState(false);

  // ========== CHARGEMENT DES DONNÉES EXISTANTES ==========
  useEffect(() => {
    loadMoisNonScolarises();
  }, []);


const loadMoisNonScolarises = async () => {
  try {
    setLoading(true);
    console.log(`📅 Récupération mois non scolarisés - Famille: ${famille.id}, Année: ${anneeScolaireId}`);
    
    // 1️⃣ CHARGER LES MOIS NON SCOLARISÉS
    const response = await api.familles.getMoisNonScolarises(famille.id, anneeScolaireId);
    
    console.log('📦 Réponse API getMoisNonScolarises:', response);
    
    // ✅ GÉRER LA STRUCTURE DE LA RÉPONSE
    const data = response?.data || response || [];
    
    // Vérifier que c'est bien un tableau
    if (!Array.isArray(data)) {
      console.error('❌ La réponse n\'est pas un tableau:', data);
      showNotification('error', 'Format de données invalide');
      return;
    }
    
    console.log('📊 Données extraites (tableau):', data);
    
    // Structurer les données par élève
    const moisParEleve = {};
    data.forEach(item => {
      if (!moisParEleve[item.eleve_id]) {
        moisParEleve[item.eleve_id] = [];
      }
      moisParEleve[item.eleve_id].push(item.mois_id);
    });
    
    setMoisNonScolarises(moisParEleve);
    console.log('📅 Mois non scolarisés chargés:', moisParEleve);
    
    // 2️⃣ CHARGER LES MOIS PAYÉS pour désactiver les checkboxes
    const moisPayesParEleve = {};
    
    console.log('💰 Chargement des mois payés...');
    
    // ✅ OPTIMISATION : Un seul appel API par mois (au lieu d'un par élève)
    for (const mois of moisScolaires) {
      try {
        const statsResponse = await api.familles.getStatsMois(
          famille.id, 
          mois.id, 
          anneeScolaireId
        );
        
        // Gérer la structure de la réponse
        const statsMoisCourant = statsResponse?.data || statsResponse;
        
        // Vérifier chaque élève dans ce mois
        if (statsMoisCourant?.eleves && Array.isArray(statsMoisCourant.eleves)) {
          statsMoisCourant.eleves.forEach(eleveData => {
            if (eleveData.statut === 'Payé') {
              if (!moisPayesParEleve[eleveData.eleve_id]) {
                moisPayesParEleve[eleveData.eleve_id] = [];
              }
              moisPayesParEleve[eleveData.eleve_id].push(mois.id);
            }
          });
        }
      } catch (error) {
        console.warn(`⚠️ Erreur chargement stats pour ${mois.nom}:`, error);
        // Continue avec les autres mois même si un échoue
      }
    }
    
    setMoisPayes(moisPayesParEleve);
    console.log('💰 Mois payés chargés:', moisPayesParEleve);
    
  } catch (error) {
    console.error('❌ Erreur chargement mois non scolarisés:', error);
    showNotification('error', 'Erreur lors du chargement des mois non scolarisés');
  } finally {
    setLoading(false);
  }
};


  // ========== TOGGLE CASE ==========
  const toggleMoisEleve = (eleveId, moisId) => {
    setModificationsEnCours(true);
    
    setMoisNonScolarises(prev => {
      const eleveMois = prev[eleveId] || [];
      const isChecked = eleveMois.includes(moisId);
      
      if (isChecked) {
        // Décocher : retirer le mois
        return {
          ...prev,
          [eleveId]: eleveMois.filter(m => m !== moisId)
        };
      } else {
        // Cocher : ajouter le mois
        return {
          ...prev,
          [eleveId]: [...eleveMois, moisId]
        };
      }
    });
  };

  // ========== VÉRIFIER SI UN MOIS EST PAYÉ ==========
  const isMoisPaye = (eleveId, moisId) => {
    // TODO: Vérifier dans les données si le mois est déjà payé
    // Pour l'instant, on permet toujours de cocher
    return false;
  };



  // ========== SAUVEGARDER LES MODIFICATIONS ==========
const handleSave = async () => {
  setSaving(true);
  try {
    // Préparer les données pour l'API
    const data = {
      annee_scolaire_id: anneeScolaireId,
      mois_non_scolarises: moisNonScolarises
    };

    console.log('💾 Sauvegarde des mois non scolarisés:', data);

    const result = await api.familles.saveMoisNonScolarises(famille.id, data);

    if (result.success) {
      console.log('✅ Sauvegarde réussie:', result.data);
      
      // ✅ Passer les données au parent (qui affichera le toast approprié)
      onSuccess?.(result.data);
      
      // Fermer le modal
      onClose();
      
    } else {
      // ❌ Erreur retournée par le backend
      console.error('❌ Erreur sauvegarde:', result.message);
      showNotification('error', result.message || 'Erreur lors de la sauvegarde');
    }
    
  } catch (error) {
    // ❌ Erreur réseau ou exception
    console.error('❌ Erreur sauvegarde:', error);
    
    // Message d'erreur personnalisé selon le type d'erreur
    let errorMessage = 'Erreur lors de la sauvegarde des mois non scolarisés';
    
    if (error.response?.status === 400) {
      errorMessage = error.response.data?.message || 'Données invalides';
    } else if (error.response?.status === 404) {
      errorMessage = 'Famille non trouvée';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    showNotification('error', errorMessage);
    
  } finally {
    setSaving(false);
  }
};


  
  // ========== COMPTER LES MODIFICATIONS ==========
  const getNombreModifications = () => {
    let count = 0;
    Object.values(moisNonScolarises).forEach(mois => {
      count += mois.length;
    });
    return count;
  };

  // ========== GROUPER LES MOIS PAR TRIMESTRE ==========
  const moisParTrimestre = moisScolaires.reduce((acc, mois) => {
    const trimestre = mois.trimestre;
    if (!acc[trimestre]) {
      acc[trimestre] = [];
    }
    acc[trimestre].push(mois);
    return acc;
  }, {});

  // Trier les mois par ordre
  Object.keys(moisParTrimestre).forEach(trimestre => {
    moisParTrimestre[trimestre].sort((a, b) => a.ordre - b.ordre);
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* EN-TÊTE */}
        <div className="p-6 bg-[#c66d59] text-white flex items-center justify-between">

          <div className="flex items-center">
            <Calendar className="w-6 h-6 mr-3" />
            <div>
              <h2 className="text-xl font-bold">Gestion des mois non scolarisés</h2>
              <p className="text-sm text-orange-100 mt-1">
                Famille {famille.nom} - {elevesFamille.length} élèves
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* INFO EXPLICATIVE */}
        <div className="p-4 bg-orange-50 border-b border-orange-200">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-orange-800">
              <p className="font-medium mb-1">Comment ça marche ?</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Cochez les mois où un élève n'est <strong>pas scolarisé</strong> (voyage, maladie longue durée, etc.)</li>
                <li>Ces mois seront <strong>exclus des statistiques d'impayés</strong></li>
                <li>Les mois déjà payés ne peuvent pas être marqués comme "non scolarisés"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CONTENU PRINCIPAL - TABLEAU */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
              <span className="ml-3 text-gray-600">Chargement des données...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* TABLEAU PAR TRIMESTRE */}
              {Object.keys(moisParTrimestre).sort().map(trimestre => (
                <div key={trimestre} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* EN-TÊTE TRIMESTRE */}
                  <div className="bg-blue-50 px-4 py-2 border-b border-blue-200">
                    <h3 className="font-semibold text-blue-900">
                      Trimestre {trimestre}
                    </h3>
                  </div>

                  {/* TABLEAU */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b-2 border-gray-200">
                        <tr>
                          <th className="py-3 px-4 text-left font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10">
                            Élève
                          </th>
                          {moisParTrimestre[trimestre].map(mois => (
                            <th 
                              key={mois.id} 
                              className="py-3 px-4 text-center font-semibold text-gray-700 min-w-[100px]"
                            >
                              {mois.nom}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {elevesFamille.map((eleve, index) => (
                          <tr 
                            key={eleve.id} 
                            className={`border-b border-gray-100 hover:bg-gray-50 ${
                              index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            }`}
                          >
                            {/* COLONNE ÉLÈVE */}
                            <td className="py-3 px-4 sticky left-0 bg-inherit z-10">
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-[#e6f1f7] text-[#3084b5] rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4" />
                                    </div>


                                <div>
                                  <div className="font-medium text-gray-900">
                                    {eleve.prenom} {eleve.nom}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {eleve.classe_nom}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* COLONNES MOIS - CHECKBOXES */}
                            {moisParTrimestre[trimestre].map(mois => {
                              const isChecked = moisNonScolarises[eleve.id]?.includes(mois.id) || false;
                              const isPaye = isMoisPaye(eleve.id, mois.id);
                              
                              return (
                                <td 
                                  key={mois.id} 
                                  className="py-3 px-4 text-center"
                                >
                                  <label className="inline-flex items-center justify-center cursor-pointer">

                                    <input
                                    type="checkbox"
                                    checked={moisNonScolarises[eleve.id]?.includes(mois.id) || false}
                                    onChange={() => toggleMoisEleve(eleve.id, mois.id)}
                                    disabled={moisPayes[eleve.id]?.includes(mois.id)}  // 🆕 DÉSACTIVER si payé
                                    className={`w-5 h-5 rounded border-2 cursor-pointer transition-all ${
                                        moisPayes[eleve.id]?.includes(mois.id)
                                        ? 'bg-gray-200 border-gray-400 cursor-not-allowed'  // 🆕 Style désactivé
                                        : 'border-[#c66d59] text-[#c66d59] focus:ring-2 focus:ring-[#c66d59] focus:ring-offset-2'
                                    }`}
                                    />

                                    {/* 🆕 BADGE "Payé" à côté de la checkbox */}
                                    {moisPayes[eleve.id]?.includes(mois.id) && (
                                    <span className="ml-2 text-[8px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">
                                        Payé
                                    </span>
                                    )}
                                  </label>
                                  {isPaye && (
                                    <div className="text-xs text-green-600 mt-1">
                                      Payé
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PIED DE PAGE - ACTIONS */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {modificationsEnCours ? (
              <span className="flex items-center text-[#c66d59] font-medium">

                <AlertCircle className="w-4 h-4 mr-2" />
                {getNombreModifications()} mois marqué{getNombreModifications() > 1 ? 's' : ''} comme non scolarisé{getNombreModifications() > 1 ? 's' : ''}
              </span>
            ) : (
              <span className="text-gray-500">
                Aucune modification
              </span>
            )}
          </div>

            <div className="flex space-x-3">
            <Button
                variant="outline"
                onClick={onClose}
                disabled={saving}
                className="border-gray-300 hover:bg-gray-100 px-4 py-2 text-sm"
            >
                Annuler
            </Button>

            <Button
                onClick={handleSave}
                disabled={saving || !modificationsEnCours}
                className="bg-[#c66d59] hover:bg-[#b85f4f] text-white
                        disabled:bg-gray-300 disabled:cursor-not-allowed
                        px-4 py-2 text-sm"
            >
                {saving ? (
                <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                </>
                ) : (
                <>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer les modifications
                </>
                )}
            </Button>
            </div>


        </div>
      </div>
    </div>
  );
};

export default ModalMoisNonScolarises;